import React, { memo, useContext, useMemo } from 'react';
import { createUseStyles, Button } from '@Sage/hoi-poi-ui';
import classNames from 'classnames';

import { CopilotContext } from 'shards/Copilot/Context';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles);

/**
 * Builds the visible label for an artifact button.
 *
 * Priority:
 * 1. Translation `label_open_artifact_<artifactType>` when available.
 * 2. Generic fallback `Open <name>` (or `Open artifact` when name is missing).
 *
 * Translations can be wired up later without touching this component.
 */
const getArtifactLabel = (artifact, t) => {
    const { artifactType, name } = artifact || {};
    const key = artifactType ? `label_open_artifact_${artifactType}` : null;
    if (key && typeof t === 'function') {
        const translated = t(key);
        if (translated && translated !== key) return translated;
    }
    if (name) return `Open ${name}`;
    if (artifactType) return `Open ${artifactType}`;
    return 'Open artifact';
};

/**
 * Renders a row of action buttons, one per Copilot artifact.
 *
 * Each button invokes the handler registered for the artifact's `artifactType`
 * via `CopilotContext`, falling back to `defaultArtifactHandler` for unknown
 * types. Visually mirrors the `Actions` component.
 */
const Artifacts = ({ artifacts, className, finished }) => {
    const { artifactHandlers, defaultArtifactHandler } = useContext(CopilotContext);
    const { t } = useWeb5Context();
    const classes = useStyles();

    const renderArtifacts = useMemo(() => {
        if (!artifacts?.length) return null;
        return artifacts
            .map((artifact, index) => {
                if (!artifact) return null;
                const handler = artifactHandlers?.[artifact.artifactType] || defaultArtifactHandler;
                if (typeof handler !== 'function') return null;

                const buttonProps = {
                    onClick: () => handler(artifact),
                    children: getArtifactLabel(artifact, t),
                    type: 'promotion',
                    size: 'small',
                };

                const key = `${artifact.download?.bucket || ''}/${artifact.download?.name || artifact.name || index
                    }`;

                return <Button key={`artifact-${key}`} {...buttonProps} />;
            })
            .filter(Boolean);
    }, [artifacts, artifactHandlers, defaultArtifactHandler, t]);

    if (!renderArtifacts || !finished) return null;

    return <div className={classNames(classes.root, className)}>{renderArtifacts}</div>;
};

Artifacts.displayName = 'Artifacts';

export default memo(Artifacts);
